import React from 'react';
import { ArrowRight, FileText, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BackgroundPattern } from './BackgroundPattern';
import { useLanguage, Language } from '../contexts/LanguageContext';
type HeroStrings = {
  eyebrow: string;
  titleLead: string;
  titleAccent: string;
  subtitle1: string;
  subtitleMid: string;
  subtitle3: string;
  ctaStart: string;
  ctaHow: string;
  ctaGuide: string;
  trust1: string;
  trust2: string;
  trust3: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, HeroStrings> = {
  en: {
    eyebrow: 'U.S. Passport Form Preparation',
    titleLead: 'We Prepare. You Submit.',
    titleAccent: 'Simple.',
    subtitle1: 'Every U.S. passport form',
    subtitleMid: '— not just the DS-11 —',
    subtitle3: 'prepared in your language and ready to submit yourself.',
    ctaStart: 'Start Your Forms',
    ctaHow: 'See How It Works',
    ctaGuide: 'Not sure which form you need?',
    trust1: 'Multilingual Support',
    trust2: 'Free Digital Delivery',
    trust3: 'Optional Printed Delivery'
  },
  es: {
    eyebrow: 'Preparación de formularios de pasaporte de EE. UU.',
    titleLead: 'Nosotros preparamos. Usted envía.',
    titleAccent: 'Fácil.',
    subtitle1: 'Cualquier formulario de pasaporte de EE. UU.',
    subtitleMid: '— no solo el DS-11 —',
    subtitle3: 'preparado en su idioma y listo para que usted lo entregue.',
    ctaStart: 'Comenzar sus formularios',
    ctaHow: 'Ver cómo funciona',
    ctaGuide: '¿No sabe qué formulario necesita?',
    trust1: 'Atención en varios idiomas',
    trust2: 'Entrega digital gratis',
    trust3: 'Entrega impresa opcional'
  },
  ht: {
    eyebrow: 'Preparasyon fòm paspò ameriken',
    titleLead: 'Nou prepare. Ou soumèt.',
    titleAccent: 'Senp.',
    subtitle1: 'Nenpòt fòm paspò ameriken',
    subtitleMid: '— pa sèlman DS-11 la —',
    subtitle3: 'prepare nan lang ou epi pare pou ou soumèt li tèt ou.',
    ctaStart: 'Kòmanse fòm ou yo',
    ctaHow: 'Gade kijan li mache',
    ctaGuide: 'Ou pa konnen ki fòm ou bezwen?',
    trust1: 'Sipò nan plizyè lang',
    trust2: 'Livrezon dijital gratis',
    trust3: 'Livrezon enprime opsyonèl'
  },
  vi: {
    eyebrow: 'Chuẩn bị mẫu đơn hộ chiếu Hoa Kỳ',
    titleLead: 'Chúng tôi chuẩn bị. Quý vị nộp.',
    titleAccent: 'Đơn giản.',
    subtitle1: 'Mọi mẫu đơn hộ chiếu Hoa Kỳ',
    subtitleMid: '— không chỉ DS-11 —',
    subtitle3: 'được chuẩn bị bằng ngôn ngữ của quý vị và sẵn sàng để quý vị tự nộp.',
    ctaStart: 'Bắt đầu mẫu đơn của quý vị',
    ctaHow: 'Xem cách hoạt động',
    ctaGuide: 'Quý vị chưa biết cần mẫu đơn nào?',
    trust1: 'Hỗ trợ đa ngôn ngữ',
    trust2: 'Giao bản kỹ thuật số miễn phí',
    trust3: 'Tùy chọn giao bản in'
  }
};
function TrustItem({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-241" data-mp="3lu9wv02xxwap">
      <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-242" data-mp="3hrlosum1wtes">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" data-id="element-243" data-mp="1xaw06rmvemay" />
      </svg>
      {label}
    </div>;
}
export function Hero() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden" data-id="element-244" data-mp="2b1r7w0lmuxcy">
      <BackgroundPattern data-id="element-245" data-mp="2g0lsn835uvzp" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-246" data-mp="2fzaov89pto8l">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-border shadow-sm mb-8" data-id="element-247" data-mp="2kxkjnhrj2mto">
          <span className="flex h-2 w-2 rounded-full bg-brand-accent" data-id="element-248" data-mp="2oye76a125zm1"></span>
          <span className="text-sm font-medium text-brand-textPrimary" data-id="element-249" data-mp="2tz1xsuldkefq">
            {t.eyebrow}
          </span>
        </div>

        <h1 className="max-w-5xl mx-auto text-5xl md:text-6xl lg:text-7xl font-bold text-brand-textPrimary tracking-tight mb-6 text-balance leading-[1.05]" data-id="element-250" data-mp="uou3jav21yby">
          {t.titleLead} <span className="text-brand-accent" data-id="element-251" data-mp="49t4lwvct2tx">{t.titleAccent}</span>
        </h1>

        <p className="max-w-2xl mx-auto text-xl md:text-2xl text-brand-textSecondary mb-12 leading-relaxed" data-id="element-252" data-mp="3vgniwwf2k3sy">
          <span className="block" data-id="element-253" data-mp="e88y26huso6l">{t.subtitle1}</span>
          <span className="block" data-id="element-254" data-mp="9aqkg2sw5ks9">{t.subtitleMid}</span>
          <span className="block" data-id="element-255" data-mp="9b0eq2kyye3r">{t.subtitle3}</span>
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-256" data-mp="3hphd6il759xm">
          <Link to="/pricing" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 focus:ring-offset-brand-bg group" data-id="element-257" data-mp="3crpijgt639pq">
            {t.ctaStart}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-258" data-mp="2tzn1i4cm5rey" />
          </Link>

          <Link to="/how-it-works" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-brand-textPrimary border border-brand-border px-8 py-4 rounded-full font-medium text-lg transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-textPrimary focus:ring-offset-2 focus:ring-offset-brand-bg" data-id="element-259" data-mp="19hvpcxf8d5ci">
            <FileText className="w-5 h-5 text-brand-textSecondary" data-id="element-260" data-mp="1sagdk1jzim9z" />
            {t.ctaHow}
          </Link>
        </div>

        <div className="mt-6" data-id="element-261" data-mp="1x7gjmcgu3p13">
          <Link to="/guia" className="inline-flex items-center gap-2 text-lg font-medium text-brand-textPrimary underline underline-offset-4 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 rounded" data-id="element-262" data-mp="1j8gul3pet84e">
            <HelpCircle className="w-5 h-5" data-id="element-263" data-mp="7vefye6vesmv" />
            {t.ctaGuide}
          </Link>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-base font-medium text-brand-textSecondary" data-id="element-264" data-mp="bsso02iteuda">
          <TrustItem label={t.trust1} data-id="element-265" data-mp="29i7inxlwy1vt" />
          <TrustItem label={t.trust2} data-id="element-266" data-mp="29j96l2o12iuy" />
          <TrustItem label={t.trust3} data-id="element-267" data-mp="24rhdakdhlz14" />
        </div>
      </div>
    </section>;
}